<!--
****** NOTES: ******
- Removed tooltip dependency (check styling...)
-->
<!-- eslint-disable vue-a11y/mouse-events-have-key-events -->
<!-- eslint-disable vue-a11y/click-events-have-key-events -->
<template>
	<div
		class="c-MetricCard"
		:class="cardClassObj">
		<p
			v-if="label"
			class="c-MetricCard__label">{{ label }}</p>
		<div
			class="c-MetricCard__container"
			@click="cardClick">
			<div class="c-MetricCard__action">
				<!-- @slot generic slot for action -->
				<slot />
			</div>
			<div
				:id="mainTitleId"
				class="c-MetricCard__mainTitle"
				@mouseenter="handleTitlesHover('mainTitle', true)"
				@mouseleave="handleTitlesHover('mainTitle', false)">
				<span
					ref="mainTitle">
					{{ mainTitle }}
				</span>
				<Tooltip
					v-if="displayMainTitleTooltip"
					:message="mainTitle"
					:position="'top'"
					:anchor-name="mainTitleId">
				</Tooltip>
			</div>
			<span class="c-MetricCard__terniaryTitle">
				{{ terniaryTitle }}
			</span>
			<div
				:id="secondaryTitleId"
				class="c-MetricCard__secondaryTitle"
				@mouseenter="handleTitlesHover('secondaryTitle', true)"
				@mouseleave="handleTitlesHover('secondaryTitle', false)">
				<span
					ref="secondaryTitle">
					{{ secondaryTitle }}
				</span>
				<Tooltip
					v-if="displaySecondaryTitleTooltip"
					:message="secondaryTitle"
					:position="'bottom'"
					:anchor-name="secondaryTitleId">
				</Tooltip>
			</div>
		</div>
		<template v-if="link">
			<div class="c-MetricCard__linkContainer">
				<button
					v-if="link"
					type="button"
					class="c-MetricCard__link"
					@click="handleClick">
					{{ link }}
				</button>
			</div>
		</template>
	</div>
</template>

<script>
import Tooltip from '../Tooltip/Tooltip.vue';
import { randomizeId } from '../../utils/utils';

export default {
  name: 'MetricCard',
  components: {
    Tooltip
  },
	props: {
		/**
     * The MetricCard type (background color)
     */
		type: {
			type: String,
			required: false,
			default: 'is-gray',
			validator(type) {
				return [
					'is-gray',
					'is-white',
				].includes(type);
			},
		},
		/**
     * The MetricCard label
     */
		label: {
			type: String,
			required: false,
			default: '',
		},
		/**
     * The MetricCard main title
     */
		mainTitle: {
			type: String,
			required: true,
			default: '',
		},
		/**
     * The MetricCard secondary title
     */
		secondaryTitle: {
			type: String,
			required: true,
			default: '',
		},
		/**
     * The MetricCard terniary title
     */
		terniaryTitle: {
			type: String,
			required: false,
			default: '',
		},
		/**
     * The MetricCard link (which only emits an event on click)
     */
		link: {
			type: String,
			required: false,
			default: undefined,
		},
		/**
     * Is the MetricCard is clickable or not
     * @values true, false
     */
		clickable: {
			type: Boolean,
			required: false,
			default: false,
		},
		/**
     * Is the MetricCard has liveData or not
     * @values true, false
     */
		liveData: {
			type: Boolean,
			required: false,
			default: false,
		},
		/**
     * MetricCard variations
     * @values normal, compact
     */
		variation: {
			type: String,
			required: false,
			default: 'normal',
			validator(variation) {
				return [
					'compact',
					'normal',
				].includes(variation);
			},
		}
	},
	data() {
		return {
      mainTitleId: randomizeId('main-title'),
      secondaryTitleId: randomizeId('secondary-title'),
      isHoveringSecondaryTitle: false,
      isHoveringTitleWrapper: false,
		}
	},
	computed: {
		cardClassObj () {
			return {
				'is-white': this.type === 'is-white',
				'is-clickable': this.clickable,
				'is-compact': this.variation === 'compact',
				'is-live-metric': this.liveData,
			};
		},
		displayMainTitleTooltip() {
      return this.isHoveringTitleWrapper
        && this.$refs.mainTitle.offsetWidth < this.$refs.mainTitle.scrollWidth
    },
    displaySecondaryTitleTooltip() {
      return this.isHoveringSecondaryTitle
        && this.$refs.secondaryTitle.offsetWidth < this.$refs.secondaryTitle.scrollWidth
    },
  },
	methods: {
		handleClick() {
			/**
       * Triggers when MetricCard link is clicked
       * @event linkClick
       */
			this.$emit('linkClick');
		},
		cardClick() {
			if (this.clickable) {
				/**
         * Triggers when MetricCard itself is clicked
         * @event buttonClick
         */
				this.$emit('buttonClick');
			}
		},
    handleTitlesHover(ref, isHovering) {
      if (ref === 'mainTitle') {
        this.isHoveringTitleWrapper = isHovering;
      }

      this.isHoveringSecondaryTitle = isHovering;
    }
  },
};
</script>

<style lang="scss" scoped>
@import '../../scss/variables.scss';

.c-MetricCard {
  $self: &;
  &.is-white {
    .c-MetricCard__container {
      border: 1px solid $un-gray-light;
      background-color: $un-white;
    }
  }

  &.is-compact {
    .c-MetricCard__container {
      height: unset;
    }
  }

  &.is-clickable {
    .c-MetricCard__container {
      cursor: pointer;

      &:hover {
        background-color: $un-n100;
        transition: .2s background-color;
      }
    }
  }

  &.is-live-metric {
    #{$self}__mainTitle {
      color: $un-gray-bd;
      &:after {
        position: absolute;
        display: inline-block;
        content: '';
        background-color: $un-green;
        border-radius: 50%;
        margin-left: 4px 0 -2px 0;
        width: 12px;
        height: 12px;
      }
    }
  }

  &__label {
    font-family: $secondary-font;
    font-size: 16px;
    letter-spacing: 0.11px;
    line-height: 21px;
    font-weight: 600;
    color: $un-gray-bd;
    margin: 0 0 $base-margin;
  }

  &__container {
    height: 155px;
    background-color: $un-n50;
    border: none;
    border-radius: $border-radius;
    padding: $base-padding;
    display: flex;
    flex-flow: column;
    justify-content: center;
    align-items: center;
    position: relative;
    transition: .2s background-color;
  }

  &__action {
    position: absolute;
    top: $base-margin;
    right: $base-margin;
  }

  &__mainTitle {
    position: relative;
    width: 100%;

    & > span {
      font-family: $secondary-font;
      font-size: 38px;
      letter-spacing: 0;
      line-height: 47px;
      font-weight: 600;
      color: $un-blue;
      width: 100%;
      text-align: center;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: block;
    }
  }

  &__secondaryTitle {
    position: relative;
    width: 100%;

    & > span {
      font-family: $secondary-font;
      font-size: 16px;
      letter-spacing: 0.11px;
      line-height: 21px;
      font-weight: 600;
      color: $un-gray-bd;
      width: 100%;
      text-align: center;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: block;
    }
  }

  &__terniaryTitle {
    font-family: $secondary-font;
    font-size: 12px;
    letter-spacing: 0.09px;
    height: 21px;
    font-weight: 600;
    color: $un-gray-bd;
    width: 100%;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__linkContainer {
    display: flex;
    justify-content: flex-end;
    margin: calc(#{$base-padding} / 2) 0 0 ;
  }

  &__link {
    cursor: pointer;
    border: none;
    background-color: transparent;
    outline: none;
    margin: 0;
    padding: 0;
    color: $un-blue;
    text-decoration: underline;
    font-size: 14px;
    letter-spacing: 0.1px;
    line-height: 21px;
  }
}
</style>
